"use client";

import * as React from "react";
import { MediaImage as Image } from "@/components/media-image";
import Link from "next/link";
import { Music2 } from "lucide-react";
import { usePlayerStore, type PlayerTrack } from "@gerama/stores";
import { NowPlayingBars } from "@gerama/ui";
import { cn, formatDuration, formatNumber } from "@gerama/utils";
import type { Song } from "@gerama/types";
import { LikeButton } from "@/features/player/like-button";
import { PlayButton } from "@/features/player/play-button";
import { songToTrack } from "@/features/player/to-track";
import { artistPath } from "@/features/artists/artist-path";
import { publicPath } from "@/lib/public-path";
import { SongContextMenu } from "./song-context-menu";

interface SongRowProps {
  song: Song;
  index?: number;
  queue: PlayerTrack[];
  contextLabel: string;
  contextHref?: string;
  /** Show the play count instead of the artists (album/playlist rows already sit under their artist). */
  hideArtists?: boolean;
}

/**
 * One row in a track list (album/playlist/search) — Spotify-style: the
 * number turns into a play button on hover, and the playing track is
 * highlighted in the brand colour.
 */
function SongRowImpl({ song, index, queue, contextLabel, contextHref, hideArtists }: SongRowProps) {
  const isCurrent = usePlayerStore(
    (s) => s.current()?.kind === "song" && s.current()?.id === song.id,
  );
  const isPlaying = usePlayerStore((s) => isCurrent && s.isPlaying);
  const track = songToTrack(song);

  return (
    <div
      className={cn(
        "hover:bg-accent/60 group flex items-center gap-3 rounded-md px-2 py-2",
        isCurrent && "bg-accent/40",
      )}
    >
      <div className="relative flex w-8 shrink-0 items-center justify-center">
        {typeof index === "number" && (
          <span
            className={cn(
              "text-sm tabular-nums group-focus-within:invisible group-hover:invisible",
              isCurrent ? "text-primary" : "text-muted-foreground",
            )}
          >
            {formatNumber(index + 1, "fa")}
          </span>
        )}
        <PlayButton
          track={track}
          queue={queue}
          contextLabel={contextLabel}
          contextHref={contextHref}
          appearance="ghost"
          className={cn(
            "absolute inset-0 m-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
            typeof index !== "number" && "static opacity-100",
          )}
        />
      </div>
      <div className="bg-muted relative size-10 shrink-0 overflow-hidden rounded">
        {song.cover ? (
          <Image src={song.cover} alt="" fill className="object-cover" sizes="40px" />
        ) : (
          <Music2 className="text-muted-foreground absolute inset-0 m-auto size-4" />
        )}
        {isCurrent && <NowPlayingBars playing={isPlaying} />}
      </div>
      <div className="min-w-0 flex-1">
        <Link
          href={publicPath("songs", song)}
          className={cn(
            "block truncate py-0.5 text-sm font-medium hover:underline",
            isCurrent && "text-primary",
          )}
        >
          {song.name}
        </Link>
        <p className="text-muted-foreground truncate text-xs">
          {!hideArtists && song.artists?.length ? (
            song.artists.map((artist, i) => (
              <React.Fragment key={artist.id}>
                {i > 0 && "، "}
                <Link
                  href={artistPath(artist)}
                  className="hover:text-foreground -my-1 inline-block py-1 hover:underline"
                >
                  {artist.name}
                </Link>
              </React.Fragment>
            ))
          ) : (
            <>{formatNumber(song.playCount, "fa")} پخش</>
          )}
        </p>
      </div>
      <LikeButton
        itemType="song"
        itemId={song.id}
        variant="icon"
        className="md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
      />
      <span className="text-muted-foreground shrink-0 text-xs tabular-nums" dir="ltr">
        {formatDuration(song.duration)}
      </span>
      <SongContextMenu
        song={song}
        triggerClassName="md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100 data-[state=open]:opacity-100"
      />
    </div>
  );
}

/** Memoised: a list re-renders when a page is appended, not every row with it. */
export const SongRow = React.memo(SongRowImpl);
